import { CommonModule } from '@angular/common';
import { Component, WritableSignal, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Timestamp } from '@angular/fire/firestore';
import { FormsModule } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { ModalController, NavParams } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { LoadingButtonComponent, LoadingService, fieldSorter } from '@scandium-oy/ngx-scandium';
import { startOfDay } from 'date-fns';
import { map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { MainDialogComponent } from 'src/app/components/main-dialog/main-dialog.component';
import { NoResultsComponent } from 'src/app/components/no-results/no-results.component';
import { SelectTicketsComponent, SelectedTicket } from 'src/app/components/select-tickets/select-tickets.component';
import { SpeechToTextComponent } from 'src/app/components/speech-to-text/speech-to-text.component';
import { ApartmentRoundReport } from 'src/app/models/apartment-round.model';
import { Contractor } from 'src/app/models/contractor.model';
import { FileUpload } from 'src/app/models/file.model';
import { MaintanceRoundReport } from 'src/app/models/maintance-round.model';
import { PlaygroundCheckReport } from 'src/app/models/playground-check.model';
import { SiteArea } from 'src/app/models/site-area.model';
import { SiteReport } from 'src/app/models/site-report.model';
import { Site } from 'src/app/models/site.model';
import { RoomTicket } from 'src/app/models/ticket.model';
import { TranslateTicketPipe } from 'src/app/pipes/ticket-translate/ticket-translate.pipe';
import { TimestampPipe } from 'src/app/pipes/timestamp/timestamp.pipe';
import { SelectDialogComponent } from 'src/app/select-dialog/select.dialog';
import { AiService } from 'src/app/services/ai.service';
import { AreasService } from 'src/app/services/areas.service';
import { ClientsService } from 'src/app/services/clients.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { PrintService } from 'src/app/services/print.service';
import { QuillService } from 'src/app/services/quill.service';
import { ScrollService } from 'src/app/services/scroll.service';
import { SiteReportsService } from 'src/app/services/site-report.service';
import { UploadService } from 'src/app/services/upload.service';
import { UsersService } from 'src/app/services/users.service';
import { WorkerTicketsService } from 'src/app/services/worker-tickets.service';
import { isApartmentRound, isPlayground } from 'src/app/utility/report';
import { defaultRoom } from 'src/app/utility/room';
import { convertTimestamp } from 'src/app/utility/time';
import { ApartmentRoundComponent } from '../apartment-round/apartment-round.component';
import { apartmentRoundCss } from '../apartment-round/apartment-round.style';
import { PrintApartmentRoundComponent } from '../apartment-round/print/print-apartment-round.component';
import { noteCss } from '../edit/note-style';
import { MaintanceRoundComponent } from '../maintance-round/maintance-round.component';
import { maintanceRoundCss } from '../maintance-round/print/maintance-round.style';
import { PrintMaintanceRoundComponent } from '../maintance-round/print/print-maintance-round.component';
import { ReportNoteComponent } from '../note/note.component';
import { playgroundItems, playgroundSteps } from '../playground/playground';
import { PlaygroundComponent } from '../playground/playground.component';
import { playgroundCss } from '../playground/print/playground.style';
import { PrintPlaygroundComponent } from '../playground/print/print-playground.component';
import { NewReportNoteDialogComponent, SiteReportNote } from './new-note.dialog';
import { css } from './report-style';

@Component({
  selector: 'app-new-site-report-dialog',
  templateUrl: './new-site-report.dialog.html',
  styleUrls: ['./new-site-report.dialog.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    MainDialogComponent,
    NoResultsComponent,
    TranslateTicketPipe,
    SelectTicketsComponent,
    TimestampPipe,
    SpeechToTextComponent,
    FormsModule,
    LoadingButtonComponent,
    ReportNoteComponent,
    ApartmentRoundComponent,
    PrintApartmentRoundComponent,
    MaintanceRoundComponent,
    PrintMaintanceRoundComponent,
    PlaygroundComponent,
    PrintPlaygroundComponent,
  ],
  providers: [TimestampPipe, QuillService],
})
export class NewSiteReportDialogComponent {
  private aiService = inject(AiService);
  private areasService = inject(AreasService);
  private clientsService = inject(ClientsService);
  private contractorsService = inject(ContractorsService);
  private loadingService = inject(LoadingService);
  private _modal = inject(ModalController);
  private modalCtrl = inject(ModalController);
  private navParams = inject(NavParams);
  private printService = inject(PrintService);
  private quillService = inject(QuillService);
  private scrollService = inject(ScrollService);
  private siteReportsService = inject(SiteReportsService);
  private timestampPipe = inject(TimestampPipe);
  private translate = inject(TranslateService);
  private uploadService = inject(UploadService);
  private usersService = inject(UsersService);
  private workerTicketsService = inject(WorkerTicketsService);

  site = this.navParams.get<Site>('site');

  areas$ = this.areasService.getSiteAreas(this.site.guid).pipe(
    map((items) => items.sort(fieldSorter(['name']))),
    shareReplay(1),
  );

  contractors$ = this.contractorsService.getList({ site: this.site.guid }).pipe(
    map((items) => items.sort(fieldSorter(['name']))),
    shareReplay(1),
  );

  readonly users = [
    this.site.manager != null ? {
      guid: this.site.manager.guid, name: this.site.manager.name, role: this.translate.instant('users.roles.manager'),
      company: this.clientsService.clientS().name, email: this.usersService.getSync(this.site.manager.guid).email,
    } : null,
    this.site.technical != null ? {
      guid: this.site.technical.guid, name: this.site.technical.name, role: this.translate.instant('users.roles.manager'),
      company: this.clientsService.clientS().name, email: this.usersService.getSync(this.site.technical.guid).email,
    } : null,
    ...this.site.managers?.map((it) => ({
      guid: it.guid, name: it.name, role: this.translate.instant('users.roles.manager'),
      company: this.clientsService.clientS().name, email: this.usersService.getSync(it.guid).email,
    })) ?? [],
    ...this.site.technicals?.map((it) => ({
      guid: it.guid, name: it.name, role: this.translate.instant('users.roles.manager'),
      company: this.clientsService.clientS().name, email: this.usersService.getSync(it.guid).email,
    })) ?? [],
    this.site.board?.chair != null ? {
      guid: this.site.board.chair.guid, name: this.site.board.chair.name,
      role: this.translate.instant('item.chair'), email: this.usersService.getSync(this.site.board.chair.guid).email,
    } : null,
    ...this.site.board?.members?.map((it) => ({
      guid: it.guid, name: it.name,
      role: this.translate.instant('item.member'), email: this.usersService.getSync(it.guid).email,
    }))
    ?? []].filter((it) => it != null);

  selectedArea = signal<SiteArea>(null);
  selectedContractor = signal<Contractor>(null);
  selectedTemplate = signal<string>(null);
  notes = signal<SiteReportNote[]>([]);
  selectedTickets = signal<RoomTicket[]>([]);
  apartmentRoundItem = signal<ApartmentRoundReport>(null);
  maintanceRoundItem = signal<MaintanceRoundReport>(null);
  playgroundItem = signal<PlaygroundCheckReport>(null);

  step = signal(1);
  loading = signal(false);
  noteText = signal('');

  tickets$ = toObservable(this.selectedContractor).pipe(
    tap(() => this.loading.set(true)),
    switchMap((contractor) => this.workerTicketsService.getMyTickets(undefined, this.site.guid).pipe(
      map((items) => items.filter((it) => contractor == null || it.contractor.guid === contractor.guid)),
    )),
    map((tickets) => tickets.map((rt) => {
      const date = convertTimestamp(rt.history[rt.history.length - 1].timestamp);
      rt.date = startOfDay(date);
      const roomName = '';
      return Object.assign({}, rt, { selected: false, roomName });
    })),
    tap(() => this.loading.set(false)),
    shareReplay(1),
  );

  readonly itemsPerPage = 3;
  readonly defaultRoom = defaultRoom;
  readonly finalStep = 4;
  readonly templates = [
    {
      name: 'tasks',
      disabled: false,
    },
    {
      name: 'note',
      disabled: false,
    },
    {
      name: 'apartmentRound',
      disabled: false,
    },
    {
      name: 'maintanceRound',
      disabled: false,
    },
    {
      name: 'playground',
      disabled: false,
    },
  ];

  readonly playgroundSteps = playgroundSteps;
  readonly playgroundItems = playgroundItems;

  role = computed(() => this.usersService.currentUserS().role);
  username = computed(() => this.usersService.currentUserS().displayName);
  readonly creationDate = new Date();

  onStep(value: number, component?: ApartmentRoundComponent | MaintanceRoundComponent | PlaygroundComponent) {
    if (this.step() === 3 && this.noteText().length > 0) {
      this.loading.set(true);
      this.aiService.doNote(this.noteText()).pipe(
        take(1),
      ).subscribe((text) => {
        this.noteText.set(text);
        this.step.update((val) => val + value);
        this.scrollService.scrollToTop();
        this.loading.set(false);
      });
    } else {
      if (component != null) {
        const report = component.getReport();
        if (isApartmentRound(report)) {
          this.apartmentRoundItem.set(report);
        } else if (isPlayground(report)) {
          this.playgroundItem.set(report);
        } else {
          this.maintanceRoundItem.set(report);
        }
      }
      this.step.update((val) => val + value);
      this.scrollService.scrollToTop();
    }
  }

  onTemplate(template: string) {
    this.selectedTemplate.set(template);
    this.step.update((val) => ++val);
  }

  selectItem(items: unknown[], item: WritableSignal<unknown>) {
    const clearButton = item() != null;

    this.modalCtrl.create({ component: SelectDialogComponent, componentProps: { items, clearButton }, cssClass: ['stack-modal'] }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.clear) {
            item.set(null);
          } else {
            item.set(data.data);
          }
        }
      });
    });
  }

  removeNote(index: number, name: string) {
    Dialog.confirm({
      message: this.translate.instant('general.delete') + ' ' + name,
      okButtonTitle: this.translate.instant('general.yes'),
      cancelButtonTitle: this.translate.instant('general.no'),
    }).then((dialog) => {
      if (dialog.value) {
        this.notes.update((val) => {
          val.splice(index, 1);
          return val;
        });
      }
    });
  }

  onNewNote() {
    this.modalCtrl.create({ component: NewReportNoteDialogComponent, componentProps: { site: this.site } }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.notes.update((val) => [...val, data.data]);
        }
      });
    });
  }

  onMic(text: string) {
    this.noteText.set(text);
  }

  onSelectTicket(roomTickets: SelectedTicket[]) {
    this.selectedTickets.set(roomTickets);
  }

  onDone() {
    const contractor = this.selectedContractor();
    const site: Site = this.navParams.get('site');
    this.loadingService.showLoading(10000, 'general.printing', ['full-loading']);
    if (this.selectedTemplate() === 'note') {
      const text = this.quillService.getHtml();
      this.noteText.set(text);
    }
    setTimeout(() => {
      if (this.selectedTemplate() === 'note') {
        const element = document.getElementById('print-notereport');
        const name = `muistio_${site.name}_${contractor?.name ?? ''}_${this.creationDate.toISOString()}`;
        const header = `${this.translate.instant('report.new.list.' + this.selectedTemplate()) as string}\n${this.timestampPipe.transform(Timestamp.now(), 'd.M.yyyy')}`;
        this.printService.printDoc(
          name, element, noteCss,
          { orientation: 'portrait', scale: 2, margins: { top: 60, right: 16, left: 16, bottom: 40 } },
          this.clientsService.clientS(), header,
        ).then((blob) => {
          this.loadingService.hideLoading();
          if (blob) {
            const file = new File([blob], `${name}.pdf`);
            const fileupload = new FileUpload(file);
            fileupload.guid = site.guid;
            this.uploadService.uploadImage(fileupload).then((url) => {
              const text = this.quillService.getHtml();
              const siteReport: SiteReport = {
                name: this.selectedTemplate(),
                site: site.guid,
                contractor: contractor ? { guid: contractor.guid, name: contractor.name } : null,
                url,
                created: Timestamp.now(),
                creator: { guid: this.usersService.currentUserS().guid, name: this.usersService.currentUserS().displayName },
                text,
              };
              this.siteReportsService.save(siteReport).then(() => this.dismiss());
            });
          }
        });
      } else if (this.selectedTemplate() === 'apartmentRound') {
        const collection = document.getElementsByClassName('page');
        const elements = Array.from(collection).map((it) => it as HTMLElement);
        const name = `huoneistokierros_${site.name}_${contractor?.name ?? ''}_${this.creationDate.toISOString()}`;
        const header = `${this.translate.instant('report.new.list.' + this.selectedTemplate()) as string}\n${this.site.name}`;
        this.printService.printDoc(
          name, elements, apartmentRoundCss,
          { orientation: 'portrait', scale: 2, margins: { top: 100, right: 8, left: 8, bottom: 40 } },
          this.clientsService.clientS(), header,
        ).then((blob) => {
          if (blob) {
            const file = new File([blob], `${name}.pdf`);
            const fileupload = new FileUpload(file);
            fileupload.guid = site.guid;
            this.uploadService.uploadImage(fileupload).then((url) => {
              const siteReport: SiteReport = {
                name: this.selectedTemplate(),
                site: site.guid,
                contractor: contractor ? { guid: contractor.guid, name: contractor.name } : null,
                url,
                created: Timestamp.now(),
                creator: { guid: this.usersService.currentUserS().guid, name: this.usersService.currentUserS().displayName },
                item: this.apartmentRoundItem(),
              };
              this.siteReportsService.save(siteReport).then(() => this.dismiss());
            });
          }
        }, (e) => {
          console.error(e);
        });
      } else if (this.selectedTemplate() === 'maintanceRound') {
        const collection = document.getElementsByClassName('page');
        const elements = Array.from(collection).map((it) => it as HTMLElement);
        const name = `kunnossapitokierros_${site.name}_${contractor?.name ?? ''}_${this.creationDate.toISOString()}`;
        const header = `${this.translate.instant('report.new.list.' + this.selectedTemplate()) as string}\n${this.site.name}`;
        this.printService.printDoc(
          name, elements, maintanceRoundCss,
          { orientation: 'portrait', scale: 2, margins: { top: 100, right: 8, left: 8, bottom: 40 } },
          this.clientsService.clientS(), header,
        ).then((blob) => {
          if (blob) {
            const file = new File([blob], `${name}.pdf`);
            const fileupload = new FileUpload(file);
            fileupload.guid = site.guid;
            this.uploadService.uploadImage(fileupload).then((url) => {
              const siteReport: SiteReport = {
                name: this.selectedTemplate(),
                site: site.guid,
                contractor: contractor ? { guid: contractor.guid, name: contractor.name } : null,
                url,
                created: Timestamp.now(),
                creator: { guid: this.usersService.currentUserS().guid, name: this.usersService.currentUserS().displayName },
                item: this.maintanceRoundItem(),
              };
              this.siteReportsService.save(siteReport).then(() => this.dismiss());
            });
          }
        }, (e) => {
          console.error(e);
        });
      } else if (this.selectedTemplate() === 'playground') {
        const collection = document.getElementsByClassName('page');
        const elements = Array.from(collection).map((it) => it as HTMLElement);
        const name = `leikkipaikkatarkastus_${site.name}_${contractor?.name ?? ''}_${this.creationDate.toISOString()}`;
        const header = `${this.translate.instant('report.new.list.' + this.selectedTemplate()) as string}\n${this.site.name}`;
        this.printService.printDoc(
          name, elements, playgroundCss,
          { orientation: 'portrait', scale: 2, margins: { top: 100, right: 8, left: 8, bottom: 40 } },
          this.clientsService.clientS(), header,
        ).then((blob) => {
          if (blob) {
            const file = new File([blob], `${name}.pdf`);
            const fileupload = new FileUpload(file);
            fileupload.guid = site.guid;
            this.uploadService.uploadImage(fileupload).then((url) => {
              const siteReport: SiteReport = {
                name: this.selectedTemplate(),
                site: site.guid,
                contractor: contractor ? { guid: contractor.guid, name: contractor.name } : null,
                url,
                created: Timestamp.now(),
                creator: { guid: this.usersService.currentUserS().guid, name: this.usersService.currentUserS().displayName },
                item: this.playgroundItem(),
              };
              this.siteReportsService.save(siteReport).then(() => this.dismiss());
            });
          }
        }, (e) => {
          console.error(e);
        });
      } else {
        const element = document.getElementById('print-taskreport');
        const name = `tehtäväraportti_${site.name}_${contractor?.name ?? ''}_${this.creationDate.toISOString()}`;
        const header = `${this.translate.instant('report.new.list.' + this.selectedTemplate()) as string}\n${this.timestampPipe.transform(Timestamp.now(), 'd.M.yyyy')}`;
        this.printService.printDoc(name, element, css, { orientation: 'portrait', scale: 2, margins: { top: 100, right: 8, left: 8, bottom: 40 } },
          this.clientsService.clientS(), header).then((blob) => {
            this.loadingService.hideLoading();
            if (blob) {
              const file = new File([blob], `${name}.pdf`);
              const fileupload = new FileUpload(file);
              fileupload.guid = site.guid;
              this.uploadService.uploadImage(fileupload).then((url) => {
                const siteReport: SiteReport = {
                  name: this.selectedTemplate(),
                  site: site.guid,
                  contractor: contractor ? { guid: contractor.guid, name: contractor.name } : null,
                  url,
                  created: Timestamp.now(),
                  creator: { guid: this.usersService.currentUserS().guid, name: this.usersService.currentUserS().displayName },
                  tickets: this.selectedTickets().map((it) => ({ guid: it.guid, parentGuid: it.guid, name: it.name })),
                };
                this.siteReportsService.save(siteReport).then(() => this.dismiss());
              });
            }
          }, (e) => {
            console.error(e);
            this.loadingService.hideLoading();
          });
      }
    }, 50);
  }

  dismiss() {
    this._modal.dismiss();
  }
}
